import {useMutation} from '@tanstack/react-query';
import {queryClient} from '@/utils/reactQueryClient';
import {
  conversationMessagesQueryKey,
  sendConversationMessage,
  sendVersionMessage,
  versionMessagesQueryKey,
} from '@/api/messages';
import {
  Message,
  SendMessagePayload,
  SendMessageResult,
} from '@/api/messages/types';
import raiseFlashMessage from '@/utils/raiseFlashMessage';

export type MessageTarget =
  | {kind: 'conversation'; id: string}
  | {kind: 'version'; id: string};

function queryKeyFor(target: MessageTarget) {
  return target.kind === 'conversation'
    ? conversationMessagesQueryKey(target.id)
    : versionMessagesQueryKey(target.id);
}

function sendFor(target: MessageTarget, payload: SendMessagePayload) {
  return target.kind === 'conversation'
    ? sendConversationMessage(target.id, payload)
    : sendVersionMessage(target.id, payload);
}

const OPTIMISTIC_ID_PREFIX = 'optimistic-';

export default function useSendMessage(
  target: MessageTarget,
  {onError}: {onError?: (e: unknown) => void} = {}
) {
  const queryKey = queryKeyFor(target);

  return useMutation({
    mutationKey: ['send-message', target.kind, target.id],
    mutationFn: (payload: SendMessagePayload) => sendFor(target, payload),
    onMutate: async (payload: SendMessagePayload) => {
      await queryClient.cancelQueries({queryKey});
      const previous = queryClient.getQueryData<Message[]>(queryKey);
      const lastOwn = [...(previous ?? [])]
        .reverse()
        .find((m) => m.fromCurrentUser);
      const optimistic: Message = {
        id: `${OPTIMISTIC_ID_PREFIX}${previous?.length ?? 0}`,
        body: payload.body,
        bodyHtml: null,
        createdAt: new Date().toISOString(),
        signerDisplayName: lastOwn?.signerDisplayName ?? null,
        contentSubmissionVersionId: null,
        sender: lastOwn?.sender ?? null,
        attachments: [],
        negotiationSnapshot: null,
        displayName: lastOwn?.displayName ?? '',
        fromCurrentUser: true,
      };
      queryClient.setQueryData<Message[]>(queryKey, (old) => [
        ...(old ?? []),
        optimistic,
      ]);
      return {previous};
    },
    onError: (err, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      onError?.(err);
    },
    onSuccess: ({message, failedAttachmentCount}: SendMessageResult) => {
      queryClient.setQueryData<Message[]>(queryKey, (old) => {
        const withoutOptimistic = (old ?? []).filter(
          (m) => !m.id.startsWith(OPTIMISTIC_ID_PREFIX)
        );
        return withoutOptimistic.some((m) => m.id === message.id)
          ? withoutOptimistic
          : [...withoutOptimistic, message];
      });
      queryClient.invalidateQueries({queryKey});
      if (failedAttachmentCount > 0) {
        raiseFlashMessage({
          status: 'error',
          message:
            failedAttachmentCount === 1
              ? 'Your message was sent, but a file couldn’t be attached. Please try attaching it again.'
              : 'Your message was sent, but some files couldn’t be attached. Please try attaching them again.',
        });
      }
    },
  });
}
